import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type {
  PayrollBatchCreateRequestDto,
  PayrollBatchDto,
} from "@/features/payroll/api/dto";

/**
 * Crée une paie en préparation à partir d'un corps JSON (`POST /PayrollBatches/add`,
 * 201). Un numéro mal formé ou en doublon est signalé sur la ligne (`INVALID`),
 * corrigeable. Pas d'idempotence côté collection v4.1 — le serveur n'engage
 * aucun débit à ce stade.
 */
export const useCreatePayrollBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "add"],
    mutationFn: (body: PayrollBatchCreateRequestDto) =>
      mutateData({
        url: payrollEndpoints.payrollBatches.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as unknown as Promise<PayrollBatchDto>,
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(), {
        resetListPage: true,
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreatePayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
